<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveCatalogStore, liveCommunitiesStore, liveMyMembershipsStore } from '../../lib/convexRealtime';
  import type { CatalogEventSummary, UserTicketSummary } from '../../lib/types';
  import { myTickets, session } from '../session';
  import { gotoTicket, gotoCommunity, gotoEvent } from '../router';
  import {
    accentStyle,
    cardAction,
    formatDayHeading,
    formatEventDate,
    formatEventMeta,
    formatPrice,
    groupEventsByDay,
    isUpcoming
  } from '../format';
  import { stripMarkdown } from '../markdown';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  const eventsStore = liveCatalogStore();
  const communitiesStore = liveCommunitiesStore();
  const membershipsStore = liveMyMembershipsStore();
  onDestroy(() => {
    eventsStore.dispose();
    communitiesStore.dispose();
    membershipsStore.dispose();
  });

  $: memberships = $membershipsStore.data ?? [];
  $: memberSlugs = new Set(memberships.map(entry => entry.community.slug));
  $: discoverCommunities = ($communitiesStore.data ?? []).filter(community => !memberSlugs.has(community.slug));

  let events: CatalogEventSummary[] = [];
  let eventsLoading = true;
  let eventsError: string | null = null;

  $: {
    events = $eventsStore.data ?? [];
    eventsLoading = $eventsStore.loading;
    eventsError = $eventsStore.error;
  }

  // Belt-and-braces: the server now excludes checkout anchors from
  // tickets:listByPerson (isCheckoutAnchor), so $myTickets never carries them.
  // This client filter is kept as defense in depth — an abandoned checkout
  // (issued row, origin 'purchase' or legacy 'self') must never surface as a
  // ticket. Redeemed rows (real guest-list membership) always stay.
  $: visibleTickets = $myTickets.tickets.filter(
    (ticket: UserTicketSummary) =>
      !((ticket.origin === 'purchase' || ticket.origin === 'self') && !ticket.redeemed)
  );

  $: ticketsByEvent = (() => {
    const map = new Map<string, UserTicketSummary[]>();
    for (const ticket of visibleTickets) {
      const list = map.get(ticket.eventId) ?? [];
      list.push(ticket);
      map.set(ticket.eventId, list);
    }
    return map;
  })();

  const startTime = (event: CatalogEventSummary) => {
    const value = new Date(event.schedule?.start ?? '').getTime();
    return Number.isNaN(value) ? Number.MAX_SAFE_INTEGER : value;
  };

  $: upcomingEvents = events.filter(event => isUpcoming(event.schedule));
  // Shotgun-style day columns: upcoming events bucketed by local calendar day,
  // days ascending, events within a day by start time. Ongoing multi-day runs
  // surface under today (handled in groupEventsByDay).
  $: dayGroups = groupEventsByDay(upcomingEvents);
  $: pastEvents = events.filter(event => !isUpcoming(event.schedule)).sort((a, b) => startTime(b) - startTime(a));

  // Price line for a card's meta row: >0 → formatted, 0 → "Free", null → omit
  // (redacted). Sold out overrides the price entirely.
  type PriceCell = { text: string; tone: 'price' | 'free' | 'soldOut' } | null;
  function priceCell(event: CatalogEventSummary): PriceCell {
    if (event.soldOut) return { text: 'Sold out', tone: 'soldOut' };
    const price = event.ticketPrice;
    if (price == null) return null;
    if (price <= 0) return { text: 'Free', tone: 'free' };
    return { text: formatPrice(price, event.currency), tone: 'price' };
  }

  function openTicket(ticket: UserTicketSummary) {
    gotoTicket(ticket.eventId, ticket.code, { push: true });
  }

  function openEvent(event: CatalogEventSummary) {
    gotoEvent(event.id, { push: true });
  }

  function eventName(eventId: string): string {
    return events.find(event => event.id === eventId)?.name ?? eventId;
  }

  function ticketStatusLabel(ticket: UserTicketSummary): string {
    return ticket.redeemed ? 'Confirmed' : 'Reserved — complete your ticket';
  }

  // Build the card CTA from a catalog event. The state→action mapping lives in
  // the shared cardAction() helper (format.ts); here we only translate this
  // page's event shape into its inputs. The card is pure navigation into
  // /e/<id> — every button below just calls gotoEvent; the event page owns the
  // real claim/apply/buy flows.
  function actionFor(event: CatalogEventSummary, owned: boolean) {
    return cardAction({
      standing: event.standing,
      soldOut: event.soldOut,
      ticketPrice: event.ticketPrice,
      applicationUrl: event.applicationUrl,
      hasCommunityApplyPath: event.community != null && event.community.visibility !== 'hidden',
      owned
    });
  }
</script>

<div class="catalog container">
  {#if $session.authLoaded && !$session.signedIn}
    <section class="catalog__hero">
      <h1>Events from communities you'll want to be part of.</h1>
      <p>Browse what's coming up, join a community, and get your ticket — sign in when you're ready.</p>
    </section>
  {/if}

  {#if memberships.length > 0}
    <section class="catalog__section" aria-labelledby="my-communities-heading">
      <h2 id="my-communities-heading">Your communities</h2>
      <div class="community-chips">
        {#each memberships as entry (entry.community.slug)}
          <button
            type="button"
            class="community-chip"
            on:click={() => gotoCommunity(entry.community.slug, { push: true })}
          >
            <span>{entry.community.name}</span>
            {#if entry.status === 'pending'}
              <span class="community-chip__pending">pending</span>
            {:else if entry.role !== 'member'}
              <span class="community-chip__role">{entry.role}</span>
            {/if}
          </button>
        {/each}
      </div>
    </section>
  {/if}

  {#if visibleTickets.length > 0}
    <section class="catalog__section" aria-labelledby="my-tickets-heading">
      <h2 id="my-tickets-heading">Your tickets</h2>
      <ul class="ticket-list">
        {#each visibleTickets as ticket (ticket.id)}
          <li>
            <button type="button" class="ticket-row surface surface--raised" on:click={() => openTicket(ticket)}>
              <span class="ticket-row__main">
                <span class="ticket-row__event">{eventName(ticket.eventId)}</span>
                <span class="ticket-row__status" class:ticket-row__status--confirmed={ticket.redeemed}>
                  {ticketStatusLabel(ticket)}
                </span>
              </span>
              <span class="ticket-row__open" aria-hidden="true">Open →</span>
            </button>
          </li>
        {/each}
      </ul>
    </section>
  {/if}

  <section class="catalog__section" aria-labelledby="events-heading">
    <h2 id="events-heading">Events</h2>

    {#if eventsError}
      <Notice variant="error">Could not load events: {eventsError}</Notice>
    {:else if eventsLoading && events.length === 0}
      <LoadingBlock label="Loading events…" />
    {:else if events.length === 0}
      <p class="catalog__empty">Nothing on the calendar right now. Check back soon.</p>
    {:else}
      {#if upcomingEvents.length === 0}
        <p class="catalog__empty">Nothing on the calendar right now. Check back soon.</p>
      {/if}
      {#each dayGroups as group (group.key)}
        <section class="day-group" aria-labelledby={`day-${group.key}`}>
          <h3 class="day-group__heading" id={`day-${group.key}`}>
            {formatDayHeading(group.date)}
          </h3>
          <div class="event-grid">
            {#each group.events as event (event.id)}
              {@const owned = ticketsByEvent.get(event.id) ?? []}
              {@const action = actionFor(event, owned.length > 0)}
              {@const price = priceCell(event)}
              {@const meta = formatEventMeta(event.schedule)}
              <!-- Stretched-link card: a non-interactive container whose title
                   link owns the whole surface (::after inset:0). The CTA button
                   sits above that pseudo-element (z-index) with its own click.
                   Everything is still pure navigation into /e/<id> — the event
                   page owns the real claim/apply/buy flows per standing. -->
              <article
                class="event-card surface surface--raised themed"
                style={accentStyle(event.community?.themeColor)}
              >
                {#if event.flyerUrl}
                  <img class="event-card__flyer" src={event.flyerUrl} alt="" loading="lazy" />
                {:else}
                  <!-- Flyerless cards keep the same footprint: an accent-tinted
                       block stands in so the grid never jumps. -->
                  <div class="event-card__flyer event-card__flyer--empty" aria-hidden="true"></div>
                {/if}
                <div class="event-card__body">
                  <div class="event-card__chips">
                    {#if event.community}
                      <span class="event-card__community">{event.community.name}</span>
                    {/if}
                    {#if action.invitedChip}
                      <span class="event-card__invited">Invited</span>
                    {/if}
                  </div>
                  <h4 class="event-card__title">
                    <a
                      class="event-card__link"
                      href={`/e/${event.id}`}
                      on:click|preventDefault={() => openEvent(event)}
                    >{event.name}</a>
                  </h4>
                  {#if event.location?.name}
                    <p class="event-card__venue">{event.location.name}</p>
                  {/if}
                  {#if meta || price}
                    <p class="event-card__meta">
                      {#if meta}<span class="event-card__when">{meta}</span>{/if}
                      {#if price}
                        <span
                          class="event-card__price"
                          class:event-card__price--free={price.tone === 'free'}
                          class:event-card__price--sold={price.tone === 'soldOut'}
                        >{price.text}</span>
                      {/if}
                    </p>
                  {/if}

                  <footer class="event-card__actions">
                    {#if action.kind === 'button'}
                      <button
                        type="button"
                        class="event-card__cta"
                        class:event-card__cta--accent={action.tone === 'accent'}
                        class:event-card__cta--success={action.tone === 'success'}
                        class:event-card__cta--ghost={action.tone === 'ghost'}
                        class:event-card__cta--disabled={action.tone === 'disabled'}
                        disabled={action.disabled}
                        on:click|stopPropagation={() => openEvent(event)}
                      >
                        {action.label}
                      </button>
                    {:else}
                      <span class="event-card__wait">{action.label}</span>
                    {/if}
                  </footer>
                </div>
              </article>
            {/each}
          </div>
        </section>
      {/each}

      {#if discoverCommunities.length > 0}
        <section class="catalog__discover catalog__section" aria-labelledby="discover-heading">
          <h2 id="discover-heading">Discover communities</h2>
          <div class="discover-grid">
            {#each discoverCommunities as community (community.slug)}
              <button
                type="button"
                class="discover-card surface"
                on:click={() => gotoCommunity(community.slug, { push: true })}
              >
                <span class="discover-card__name">{community.name}</span>
                {#if community.description}
                  <span class="discover-card__description">{stripMarkdown(community.description)}</span>
                {/if}
                <span class="discover-card__meta">
                  {community.visibility === 'public' ? 'Open to join' : 'Apply to join'}
                  {#if community.memberCount != null}
                    · {community.memberCount} {community.memberCount === 1 ? 'member' : 'members'}
                  {/if}
                </span>
              </button>
            {/each}
          </div>
        </section>
      {/if}

      {#if pastEvents.length > 0}
        <details class="catalog__past">
          <summary>Past events ({pastEvents.length})</summary>
          <ul class="past-list">
            {#each pastEvents as event (event.id)}
              <li>
                <span class="past-list__name">{event.name}</span>
                {#if formatEventDate(event.schedule)}
                  <span class="past-list__date">{formatEventDate(event.schedule)}</span>
                {/if}
              </li>
            {/each}
          </ul>
        </details>
      {/if}
    {/if}
  </section>
</div>

<style>
  .catalog {
    display: flex;
    flex-direction: column;
    gap: var(--space-7);
    padding: var(--space-6) var(--space-4) var(--space-8);
  }

  .catalog__hero {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-5) 0 0;
    max-width: 56ch;
  }

  .catalog__hero h1 {
    margin: 0;
    font-family: var(--font-display);
    font-size: clamp(1.6rem, 4vw, 2.2rem);
    line-height: 1.2;
  }

  .catalog__hero p {
    margin: 0;
    color: var(--color-text-secondary);
    font-size: var(--font-size-md);
    line-height: var(--line-height-relaxed);
  }

  .catalog__section {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .catalog__section h2 {
    margin: 0;
    font-size: var(--font-size-xl);
  }

  .ticket-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .ticket-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-4) var(--space-5);
    border-radius: var(--radius-lg);
    border: var(--border-subtle);
    cursor: pointer;
    text-align: left;
    color: var(--color-text-primary);
    background: var(--color-surface-glass);
    transition: border-color var(--transition-fast), transform var(--transition-fast);
  }

  .ticket-row:hover {
    border-color: rgb(var(--accent-rgb) / 0.4);
    transform: translateY(-1px);
  }

  .ticket-row__main {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .ticket-row__event {
    font-weight: 600;
    font-size: var(--font-size-md);
  }

  .ticket-row__status {
    font-size: var(--font-size-sm);
    color: var(--color-text-amber);
  }

  .ticket-row__status--confirmed {
    color: var(--color-success-500);
  }

  .ticket-row__open {
    flex-shrink: 0;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  /* Day columns: TUE 7 JUL heading + its own card grid, stacked down the page.
     The wrapping .catalog__section already gaps sections; day groups sit inside
     it with their own tighter rhythm. */
  .day-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .day-group:not(:first-of-type) {
    margin-top: var(--space-6);
  }

  /* Sticky day header. Sits BELOW the shell header (--shell-header-h) so cards
     scroll under it cleanly. Solid ink background + hairline while stuck.
     Cards are positioned (stretched-link pattern, CTA z-index 1), so the
     heading must sit above them — but still below the shell header (z-raised). */
  .day-group__heading {
    position: sticky;
    top: var(--shell-header-h);
    z-index: calc(var(--z-raised) - 1);
    margin: 0;
    padding: var(--space-3) 0;
    background: var(--color-surface-950);
    border-bottom: var(--border-subtle);
    font-family: var(--font-display);
    font-size: var(--font-size-xl);
    font-weight: 800;
    line-height: var(--line-height-tight);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-primary);
  }

  .event-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--space-5);
  }

  @media (max-width: 1100px) {
    .event-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 640px) {
    .event-grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  /* Mobile (≤600px): the shell header goes static and scrolls away (AppShell),
     so the day heading must stick to the very top — its --shell-header-h offset
     would otherwise leave a gap where the unpinned header used to be. */
  @media (max-width: 600px) {
    .day-group__heading {
      top: 0;
    }
  }

  /* Non-interactive container for the stretched-link pattern. The title link
     stretches across the whole surface; the CTA button layers above it. */
  .event-card {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    color: var(--color-text-primary);
    border: var(--border-subtle);
    overflow: hidden;
    transition: border-color var(--transition-fast), transform var(--transition-fast);
  }

  /* Hover lift stays on the container, keyed off the title link's hover so the
     whole card responds to pointing at its clickable surface. */
  .event-card:hover {
    border-color: rgb(var(--accent-rgb) / 0.4);
    transform: translateY(-1px);
  }

  /* Landscape flyer crop, flush to the card top. Flyerless cards get an
     accent-tinted placeholder of the SAME aspect so the grid never jumps. */
  .event-card__flyer {
    width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    display: block;
    border-bottom: var(--border-subtle);
  }

  .event-card__flyer--empty {
    background:
      linear-gradient(135deg, rgb(var(--accent-rgb) / 0.14), rgb(var(--accent-rgb) / 0.02)),
      var(--color-surface-900);
  }

  .event-card__body {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-4);
    flex: 1;
  }

  /* Title is the single strongest text on the card, and the card's primary
     interactive element. */
  .event-card__title {
    margin: 0;
    font-size: var(--font-size-lg);
    font-weight: 700;
    line-height: var(--line-height-snug);
  }

  /* The title link is a real <a href="/e/<id>"> (cmd/middle-click work, survives
     full page loads) intercepted into gotoEvent. Its ::after stretches over the
     whole card so the entire surface navigates; the CTA button layers above it. */
  .event-card__link {
    color: inherit;
    text-decoration: none;
  }

  .event-card__link::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
  }

  .event-card__link:focus-visible {
    outline: none;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .event-card__chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-2);
  }

  /* Venue directly under the title, muted grey. Absent when redacted. */
  .event-card__venue {
    margin: 0;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  /* Meta line: accent date/time + mono price, on one baseline. */
  .event-card__meta {
    margin: var(--space-1) 0 0;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2) var(--space-3);
    font-size: var(--font-size-sm);
  }

  .event-card__when {
    color: var(--color-accent-text);
    font-family: var(--font-mono);
  }

  .event-card__price {
    font-family: var(--font-mono);
    color: var(--color-text-primary);
  }

  .event-card__price--free {
    color: var(--color-success-500);
  }

  .event-card__price--sold {
    color: var(--color-text-muted);
  }

  /* Fixed min-height so text-only wait states (no block button) keep the same
     footprint as button cards — the grid never jumps between the two. */
  .event-card__actions {
    margin-top: auto;
    padding-top: var(--space-3);
    display: flex;
    align-items: center;
    min-height: calc(var(--space-3) + 2.6rem);
  }

  /* The CTA button layers ABOVE the title link's stretched pseudo-element
     (z-index:1) so it takes its own clicks. Full-width block (Shotgun style),
     sharp corners (house style). Explicit background on every variant so the
     global bare-button accent rule never leaks through. */
  .event-card__cta {
    position: relative;
    z-index: 1;
    width: 100%;
    padding: 0.7rem 1rem;
    border: 1px solid transparent;
    border-radius: 0;
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    font-weight: 700;
    letter-spacing: 0.02em;
    cursor: pointer;
    text-align: center;
    transition: background var(--transition-fast), border-color var(--transition-fast),
      color var(--transition-fast);
  }

  /* Primary buy/apply: the card's own community accent with dark ink text. */
  .event-card__cta--accent {
    background: rgb(var(--accent-rgb));
    color: var(--color-surface-950);
  }

  .event-card__cta--accent:hover {
    background: color-mix(in srgb, rgb(var(--accent-rgb)), white 14%);
  }

  /* "You're going": same block, success tone (not the accent). */
  .event-card__cta--success {
    background: color-mix(in srgb, var(--color-success-500), transparent 82%);
    border-color: color-mix(in srgb, var(--color-success-500), transparent 45%);
    color: var(--color-success-500);
    cursor: default;
  }

  /* Ghost: "Manage event" / "View event" — transparent, subtle border. */
  .event-card__cta--ghost {
    background: transparent;
    border-color: rgb(var(--accent-rgb) / 0.3);
    color: var(--color-text-secondary);
  }

  .event-card__cta--ghost:hover {
    border-color: rgb(var(--accent-rgb) / 0.5);
    color: var(--color-text-primary);
  }

  /* Disabled "Sold out": surface-toned, muted, no pointer. */
  .event-card__cta--disabled,
  .event-card__cta--disabled:hover {
    background: var(--color-surface-800);
    color: var(--color-text-muted);
    cursor: default;
  }

  /* Quiet wait states ("By invitation", "Application pending") — muted text in
     the same footer slot, no button chrome. */
  .event-card__wait {
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  .catalog__empty {
    margin: 0;
    color: var(--color-text-muted);
  }

  .community-chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .community-chip {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-pill);
    border: var(--border-subtle);
    background: var(--color-surface-glass);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
    font-weight: 600;
    cursor: pointer;
    transition: border-color var(--transition-fast);
  }

  .community-chip:hover {
    border-color: rgb(var(--accent-rgb) / 0.4);
  }

  .community-chip__pending {
    font-size: var(--font-size-2xs);
    color: var(--color-text-amber);
    font-weight: 400;
  }

  .community-chip__role {
    font-size: var(--font-size-2xs);
    color: var(--color-accent-neon-400);
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .event-card__community {
    display: inline-block;
    padding: 2px var(--space-3);
    border-radius: var(--radius-pill);
    /* Per-community accent: the card's inline --accent-rgb override
       (accentStyle) resolves here — rgb(var()) at the point of use. */
    border: 1px solid rgb(var(--accent-rgb) / 0.35);
    background: rgb(var(--accent-rgb) / 0.06);
    color: color-mix(in srgb, rgb(var(--accent-rgb)), white 18%);
    font-size: var(--font-size-2xs);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  /* "Invited" status chip: mirrors the community chip's shape but reads as a
     filled accent status (not a second button) so the invite is visible before
     the click. Sits next to the community chip in the chips row. */
  .event-card__invited {
    display: inline-block;
    padding: 2px var(--space-3);
    border-radius: var(--radius-pill);
    border: 1px solid transparent;
    background: rgb(var(--accent-rgb) / 0.18);
    color: color-mix(in srgb, rgb(var(--accent-rgb)), white 24%);
    font-size: var(--font-size-2xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .discover-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: var(--space-4);
  }

  .discover-card {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-4) var(--space-5);
    border-radius: var(--radius-lg);
    text-align: left;
    cursor: pointer;
    color: var(--color-text-primary);
    background: var(--color-surface-900);
    font: inherit;
    border: var(--border-subtle);
    transition: border-color var(--transition-fast), transform var(--transition-fast);
  }

  .discover-card:hover {
    border-color: rgb(var(--accent-rgb) / 0.4);
    transform: translateY(-1px);
  }

  .discover-card__name {
    font-weight: 600;
  }

  .discover-card__description {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
  }

  .discover-card__meta {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .catalog__past summary {
    cursor: pointer;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  .past-list {
    list-style: none;
    margin: var(--space-3) 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .past-list li {
    display: flex;
    justify-content: space-between;
    gap: var(--space-4);
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    padding: var(--space-2) 0;
    border-bottom: var(--border-subtle);
  }
</style>
